import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionHeading from '../../../MyComponents/LandingSections/SectionHeading';
import { FEATURES_LINK, FOR_SCHOOLS_LINK, FOR_TUTORING_LINK, TUTORIAL_LINK } from '../../../common/urlLinks';
import { transitionToTop } from '../../../common/utils';
import { CARD, PILL_SECONDARY, TEXT_LINK } from './dashboardStyles';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS } from '../../../constants';

const IMG = '/static/asset/img/';

// The same six destinations the old welcome blocks pointed at; only the
// framing is new. Labels keep their `welcome.text.*` keys.
const TILES = [
    {
        key: 'schools',
        labelId: 'welcome.text.exploreSchools',
        img: `${IMG}schools/orice_smartboard.png`,
        tint: REDESIGN_TINTS.mint,
        to: FOR_SCHOOLS_LINK,
    },
    {
        key: 'tutoring',
        labelId: 'welcome.text.exploreTutoring',
        img: `${IMG}features/formule_matematice.png`,
        tint: REDESIGN_TINTS.peach,
        to: FOR_TUTORING_LINK,
    },
    {
        key: 'individualBoards',
        labelId: 'welcome.text.exploreIndividualBoards',
        img: `${IMG}tutoring/elevi_diferite_nivele.png`,
        tint: REDESIGN_TINTS.sky,
        to: `${FEATURES_LINK}?feature=individualBoards`,
    },
    {
        key: 'library',
        labelId: 'welcome.text.exploreLibrary',
        img: `${IMG}features/librarie.png`,
        tint: REDESIGN_TINTS.amber,
        to: `${FEATURES_LINK}?feature=library`,
    },
    {
        key: 'compose',
        labelId: 'welcome.text.exploreCompose',
        img: `${IMG}features/lipiti_informatii.png`,
        tint: REDESIGN_TINTS.periwinkle,
        to: `${FEATURES_LINK}?feature=paste`,
    },
    {
        key: 'keepInTouch',
        labelId: 'welcome.text.keepInTouch',
        img: `${IMG}tutoring/conferinta_audio_video.png`,
        tint: REDESIGN_TINTS.linen,
        to: `${FEATURES_LINK}?feature=audioVideo`,
    },
];

const useStyles = makeStyles(() => ({
    ctaRow: {
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
    },
    cta: PILL_SECONDARY,
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: '18px',
    },
    more: {
        ...TEXT_LINK,
        marginTop: 'auto',
        fontSize: '0.86rem',
    },
    tile: {
        ...CARD,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textDecoration: 'none',
        transition: 'border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
        '&:hover': {
            borderColor: REDESIGN_COLORS.primary,
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 32px rgba(6, 63, 69, 0.09)',
        },
        '&:hover $more': {
            color: REDESIGN_COLORS.primary,
        },
    },
    // A fixed-height mat with the illustration fitted inside it. The image is
    // sized explicitly (not max-*) because percentage max-heights do not
    // resolve inside an auto-sized flex/grid row and the drawing would spill
    // over the copy on narrow screens.
    media: {
        boxSizing: 'border-box',
        height: '150px',
        padding: '18px',
    },
    image: {
        display: 'block',
        width: '100%',
        height: '100%',
        objectFit: 'contain',
    },
    body: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '18px 20px 20px',
    },
    label: {
        margin: '0 0 6px',
        fontSize: '1.05rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: '0 0 12px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        lineHeight: 1.5,
    },
}));

/**
 * "What else VBoard can do": the tutorial as the main door, and six tiles
 * that jump to the school and tutoring pages or straight to a feature.
 */
const ExploreCards = () => {
    const classes = useStyles();
    const intl = useIntl();

    return (
        <LandingSection id="explore" background="none" gutter={0} paddingTop={16} paddingBottom={48}>
            <SectionHeading
                eyebrow={<FormattedMessage id="redesign.home.dashboard.explore.eyebrow" />}
                title={<FormattedMessage id="redesign.home.dashboard.explore.title" />}
                lead={<FormattedMessage id="redesign.home.dashboard.explore.lead" />}
                titleSize="clamp(1.7rem, 3.2vw, 2.5rem)"
                marginBottom={22}
            />
            <div className={classes.ctaRow}>
                <Link to={TUTORIAL_LINK} onClick={transitionToTop} className={classes.cta}>
                    <FormattedMessage id="redesign.home.dashboard.explore.tutorial" />
                    <span aria-hidden="true">→</span>
                </Link>
            </div>

            <div className={classes.grid}>
                {TILES.map(({ key, labelId, img, tint, to }) => (
                    <Link key={key} to={to} onClick={transitionToTop} className={classes.tile}>
                        <div className={classes.media} style={{ background: tint }}>
                            <img
                                src={img}
                                alt={intl.formatMessage({ id: labelId })}
                                loading="lazy"
                                decoding="async"
                                className={classes.image}
                            />
                        </div>
                        <div className={classes.body}>
                            <h3 className={classes.label}>
                                <FormattedMessage id={labelId} />
                            </h3>
                            <p className={classes.text}>
                                <FormattedMessage id={`redesign.home.dashboard.explore.${key}.text`} />
                            </p>
                            <span className={classes.more}>
                                <FormattedMessage id="redesign.home.dashboard.explore.more" />
                                <span aria-hidden="true">→</span>
                            </span>
                        </div>
                    </Link>
                ))}
            </div>
        </LandingSection>
    );
};

export default ExploreCards;
